import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Activity, Cpu, HardDrive, Mic, Brain, Database,
  TrendingUp, AlertTriangle, CheckCircle, ArrowUpRight,
  RefreshCw, IndianRupee, DollarSign, Zap, Shield, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export default function SystemMonitorPage() {
  const [usage, setUsage] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [showINR, setShowINR] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usageRes, configRes] = await Promise.all([
        fetch(`${API}/api/admin/system-usage?days=${days}`, {
          credentials: 'include',
        }),
        fetch(`${API}/api/admin/system-config`, {
          credentials: 'include',
        }),
      ]);
      if (usageRes.ok) setUsage(await usageRes.json());
      if (configRes.ok) setConfig(await configRes.json());
    } catch (e) {
      console.error('Failed to fetch system data:', e);
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !usage) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="system-monitor-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-sm text-slate-500">Loading system data...</span>
      </div>
    );
  }

  const totals = usage?.costs?._totals || {};
  const suggestions = config?.upgrade_suggestions || [];
  const upgradeCount = suggestions.filter(s => s.recommendation === 'upgrade' || s.recommendation === 'consider').length;
  const pendingCount = suggestions.filter(s => s.recommendation === 'pending').length;
  const activeStack = config?.config?.active_stack || {};

  return (
    <div className="space-y-6 max-w-6xl" data-testid="system-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">System Monitor</h1>
          <p className="text-sm text-slate-500">Usage metrics, cost estimates & upgrade suggestions</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-xs border rounded-lg px-2.5 py-1.5 bg-white text-slate-600"
            data-testid="period-selector"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8" data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Cost Summary Banner */}
      <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="cost-banner">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Estimated Cost ({days}d)</p>
                <p className="text-2xl font-bold text-emerald-800" data-testid="total-cost">
                  {showINR ? `₹${totals.total_inr?.toLocaleString() || '0'}` : `$${totals.total_usd?.toLocaleString() || '0'}`}
                </p>
              </div>
              <div className="h-10 w-px bg-emerald-200" />
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="text-emerald-500">LLM</p>
                  <p className="font-semibold text-slate-700">{showINR ? `₹${(totals.llm_usd * 85)?.toFixed(0) || '0'}` : `$${totals.llm_usd || '0'}`}</p>
                </div>
                <div>
                  <p className="text-emerald-500">Speech</p>
                  <p className="font-semibold text-slate-700">{showINR ? `₹${(totals.stt_usd * 85)?.toFixed(0) || '0'}` : `$${totals.stt_usd || '0'}`}</p>
                </div>
                <div>
                  <p className="text-emerald-500">Storage</p>
                  <p className="font-semibold text-slate-700">{showINR ? `₹${(totals.storage_usd * 85)?.toFixed(0) || '0'}` : `$${totals.storage_usd || '0'}`}</p>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-emerald-600"
              onClick={() => setShowINR(!showINR)}
              data-testid="currency-toggle"
            >
              {showINR ? <DollarSign className="w-3.5 h-3.5 mr-1" /> : <IndianRupee className="w-3.5 h-3.5 mr-1" />}
              Show in {showINR ? 'USD' : 'INR'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="upgrades" data-testid="tab-upgrades">
            Upgrades
            {upgradeCount > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-[9px] px-1.5">{upgradeCount}</Badge>}
            {pendingCount > 0 && <Badge className="ml-1 bg-purple-500 text-white text-[9px] px-1.5">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="stack" data-testid="tab-stack">Active Stack</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-stats">
            <StatCard
              icon={Activity}
              label="Consults Today"
              value={usage?.today_consults || 0}
              sub={`${usage?.db_stats?.consult_cards || 0} total cards`}
              color="emerald"
            />
            <StatCard
              icon={Brain}
              label="LLM Calls"
              value={formatNumber(Object.values(usage?.usage?.llm || {}).reduce((a, b) => a + (b.calls || 0), 0))}
              sub={`${days}d period`}
              color="violet"
            />
            <StatCard
              icon={Mic}
              label="Transcriptions"
              value={formatNumber(Object.values(usage?.usage?.stt || {}).reduce((a, b) => a + (b.calls || 0), 0))}
              sub={`${Math.round(Object.values(usage?.usage?.stt || {}).reduce((a, b) => a + (b.audio_seconds || 0), 0) / 60)} min audio`}
              color="blue"
            />
            <StatCard
              icon={HardDrive}
              label="Files Stored"
              value={(usage?.file_counts?.media_attachments || 0) + (usage?.file_counts?.audio_sessions || 0)}
              sub={usage?.storage_connected ? 'Cloud connected' : 'Local only'}
              color={usage?.storage_connected ? 'emerald' : 'amber'}
            />
          </div>

          {/* Database Stats */}
          <Card data-testid="db-stats">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Database className="w-4 h-4 text-slate-500" /> Database Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {Object.entries(usage?.db_stats || {}).map(([col, count]) => (
                  <div key={col} className="text-center p-2 rounded-lg bg-slate-50">
                    <p className="text-lg font-bold text-slate-800">{formatNumber(count)}</p>
                    <p className="text-[10px] text-slate-500 capitalize">{col.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* LLM Usage Breakdown */}
          {Object.keys(usage?.usage?.llm || {}).length > 0 && (
            <Card data-testid="llm-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain className="w-4 h-4 text-violet-500" /> LLM Usage Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(usage.usage.llm).map(([model, data]) => (
                    <div key={model} className="flex items-center justify-between p-3 rounded-lg bg-violet-50/50 border border-violet-100">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{model}</p>
                        <div className="flex gap-2 mt-1">
                          {(data.categories || []).map(c => (
                            <Badge key={c} variant="outline" className="text-[9px] border-violet-200 text-violet-600">{c}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-violet-700">{formatNumber(data.calls)}</p>
                        <p className="text-[10px] text-slate-500">calls</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* STT Usage */}
          {Object.keys(usage?.usage?.stt || {}).length > 0 && (
            <Card data-testid="stt-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Mic className="w-4 h-4 text-blue-500" /> Speech-to-Text Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(usage.usage.stt).map(([model, data]) => (
                    <div key={model} className="flex items-center justify-between p-3 rounded-lg bg-blue-50/50 border border-blue-100">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{model}</p>
                        <p className="text-[10px] text-slate-500">{Math.round(data.audio_seconds / 60)} min transcribed</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-700">{formatNumber(data.calls)}</p>
                        <p className="text-[10px] text-slate-500">calls</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Storage Usage */}
          {Object.keys(usage?.usage?.storage || {}).length > 0 && (
            <Card data-testid="storage-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-teal-500" /> Storage Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(usage.usage.storage).map(([cat, data]) => (
                    <div key={cat} className="p-3 rounded-lg bg-teal-50/50 border border-teal-100">
                      <p className="text-sm font-semibold text-slate-800 capitalize">{cat.replace(/_/g, ' ')}</p>
                      <p className="text-lg font-bold text-teal-700 mt-1">{data.uploads || 0} uploads</p>
                      <p className="text-[10px] text-slate-500">{formatBytes(data.upload_bytes)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty state when no usage tracked */}
          {Object.keys(usage?.usage?.llm || {}).length === 0 &&
           Object.keys(usage?.usage?.stt || {}).length === 0 && (
            <Card className="border-dashed" data-testid="no-usage-data">
              <CardContent className="py-8 text-center">
                <Activity className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-500">No usage data tracked yet</p>
                <p className="text-xs text-slate-400 mt-1">Usage metrics will appear here as the system processes consultations, transcriptions, and AI requests.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* UPGRADES TAB */}
        <TabsContent value="upgrades" className="space-y-4">
          {suggestions.length === 0 ? (
            <Card className="border-emerald-200" data-testid="no-upgrades">
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-emerald-700">All systems up to date</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Worth considering</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Not recommended</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" /> Coming soon</span>
              </div>
              <div className="space-y-3" data-testid="upgrade-suggestions">
                {suggestions.map((s, i) => (
                  <UpgradeCard key={i} suggestion={s} />
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ACTIVE STACK TAB */}
        <TabsContent value="stack" className="space-y-3">
          <div className="grid gap-3" data-testid="active-stack">
            {Object.entries(activeStack).map(([key, item]) => (
              <Card key={key} className="border-slate-200">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-800">{item.label}</p>
                        <CategoryBadge category={item.category} />
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{item.purpose}</p>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <Badge variant="outline" className="text-[10px] font-mono border-slate-300">{item.provider}/{item.model}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'slate' }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    violet: 'bg-violet-50 text-violet-600 border-violet-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    slate: 'bg-slate-50 text-slate-600 border-slate-100',
  };
  return (
    <Card className={`border ${colors[color]?.split(' ')[2] || 'border-slate-100'}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-3.5 h-3.5 ${colors[color]?.split(' ')[1] || 'text-slate-500'}`} />
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</span>
        </div>
        <p className="text-xl font-bold text-slate-800">{value}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}

function UpgradeCard({ suggestion: s }) {
  const recColors = {
    upgrade: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', icon: ArrowUpRight, iconColor: 'text-emerald-500' },
    consider: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: TrendingUp, iconColor: 'text-amber-500' },
    evaluate: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', icon: Zap, iconColor: 'text-blue-500' },
    avoid: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: Shield, iconColor: 'text-red-500' },
    pending: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', icon: Clock, iconColor: 'text-purple-500' },
  };
  const c = recColors[s.recommendation] || recColors.evaluate;
  const Icon = c.icon;

  return (
    <Card className={`${c.border} ${c.bg}`} data-testid={`upgrade-${s.component}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-1.5 rounded-lg ${c.bg}`}>
            <Icon className={`w-4 h-4 ${c.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-slate-800">{s.component_label}</p>
              <Badge className={`text-[9px] ${c.badge}`}>{s.recommendation.toUpperCase()}</Badge>
              <CategoryBadge category={s.category} />
            </div>
            <p className="text-xs text-slate-600 mt-1">{s.improvement}</p>
            <div className="flex items-center gap-4 mt-2 text-[10px]">
              <span className="text-slate-400">Current: <span className="font-mono text-slate-600">{s.current_model}</span></span>
              <span className="text-slate-400">{s.recommendation === 'pending' ? 'Upcoming' : 'Suggested'}: <span className="font-mono text-slate-600">{s.suggested_model}</span></span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{s.cost_change}</p>
            {s.note && (
              <div className={`flex items-center gap-1 mt-1.5 text-[10px] ${s.recommendation === 'pending' ? 'text-purple-600' : 'text-amber-600'}`}>
                {s.recommendation === 'pending' ? <Clock className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {s.note}
              </div>
            )}
          </div>
          {s.risk !== 'n/a' && (
            <div className="shrink-0">
              <Badge variant="outline" className={`text-[9px] ${s.risk === 'low' ? 'border-emerald-300 text-emerald-600' : s.risk === 'medium' ? 'border-amber-300 text-amber-600' : 'border-red-300 text-red-600'}`}>
                {s.risk} risk
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryBadge({ category }) {
  const styles = {
    critical: 'bg-red-100 text-red-600',
    standard: 'bg-slate-100 text-slate-600',
    background: 'bg-slate-100 text-slate-500',
    infrastructure: 'bg-blue-100 text-blue-600',
  };
  return (
    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wider ${styles[category] || styles.standard}`}>
      {category}
    </span>
  );
}
